export type DisplaySlaState = {
  status: "none" | "on_track" | "at_risk" | "breached";
  targetAt?: string;
  minutesRemaining?: number;
  ruleName?: string;
  thresholdHours?: number;
};

type EntitySlaInput = {
  targetAt?: string;
  breachedAt?: string;
  now?: Date;
  atRiskWindowMinutes?: number;
};

export function formatSlaLabel(slaState: DisplaySlaState) {
  const minutes = slaState.minutesRemaining;
  if (slaState.status === "none") {
    return "No SLA";
  }
  if (slaState.status === "breached") {
    return "SLA breached";
  }
  if (slaState.status === "at_risk") {
    if (minutes !== undefined) {
      return `SLA at risk (${minutes} min left)`;
    }
    return "SLA at risk";
  }
  if (minutes !== undefined) {
    return `SLA on track (${minutes} min left)`;
  }
  return "SLA on track";
}

export function formatSlaBreachLabel(minutes?: number) {
  if (minutes === undefined) {
    return undefined;
  }
  if (minutes <= 0) {
    return "SLA breach imminent";
  }
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours > 0 && remainder > 0) {
    return `SLA breach in ${hours}h ${remainder}m`;
  }
  if (hours > 0) {
    return `SLA breach in ${hours}h`;
  }
  return `SLA breach in ${remainder}m`;
}

export function getSlaStateFromEntity(input: EntitySlaInput): DisplaySlaState {
  if (!input.targetAt) {
    return { status: "none" };
  }

  const now = input.now ?? new Date();
  const target = new Date(input.targetAt);
  const minutesRemaining = Math.max(
    Math.ceil((target.getTime() - now.getTime()) / 60000),
    0
  );

  if (input.breachedAt || target.getTime() <= now.getTime()) {
    return {
      status: "breached",
      targetAt: input.targetAt,
      minutesRemaining,
    };
  }

  const atRiskWindowMinutes = input.atRiskWindowMinutes ?? 60;
  return {
    status: minutesRemaining <= atRiskWindowMinutes ? "at_risk" : "on_track",
    targetAt: input.targetAt,
    minutesRemaining,
  };
}

export function getSlaBadgeClassName(status: DisplaySlaState["status"]) {
  if (status === "breached") {
    return "border-red-500/40 bg-red-950/40 text-red-100";
  }
  if (status === "at_risk") {
    return "border-amber-500/40 bg-amber-950/30 text-amber-100";
  }
  return "text-foreground/90";
}
